// Common API-error → interface-language message. Backend codes, response
// contracts and business logic are untouched: English keeps today's text;
// Finnish maps known codes / known exact English texts, otherwise a safe
// status-based message (never raw technical details or customer text).
import i18n from "@/i18n";
import { formatApiErrorDetail } from "@/lib/api";

// Exact backend English text → translation key (extended per module batch).
const KNOWN_TEXT = {
    "Invalid email or password": "errors.text.invalidCredentials",
    "Too many failed login attempts. Try again later.": "errors.text.tooManyLoginAttempts",
    "Not authenticated": "errors.text.notAuthenticated",
    "Session expired": "errors.text.sessionExpired",
    "Token expired": "errors.text.sessionExpired",
    "Account disabled": "errors.text.accountDisabled",
    "Name cannot be empty": "errors.text.nameEmpty",
    "Current password is incorrect": "errors.text.currentPasswordIncorrect",
    "language_preference must be en or fi": "errors.text.languageInvalid",
    "Delivery not found": "errors.text.deliveryNotFound",
    "Item not found": "errors.text.itemNotFound",
    "Document not found": "errors.text.documentNotFound",
    "File no longer available": "errors.text.fileNoLongerAvailable",
    "Extraction job not found": "errors.text.extractionJobNotFound",
    "Document storage temporarily unavailable": "errors.text.documentStorageUnavailable",
    "Document storage error": "errors.text.documentStorageError",
    "Product name is required": "errors.text.productNameRequired",
    "Supplier is required": "errors.text.supplierRequired",
    "Only JPG, PNG or PDF are supported": "errors.text.fileTypeUnsupported",
    "File too large (max 20 MB)": "errors.text.fileTooLarge20",
    "This delivery has no items.": "errors.text.deliveryNoItems",
    "Cannot complete a delivery without items.": "errors.text.cannotCompleteWithoutItems",
    "Attach at least one document before queueing OCR": "errors.text.attachDocBeforeOcr",
    "At least one item is missing a unit.": "errors.text.itemMissingUnit",
    "At least one item is missing a product name.": "errors.text.itemMissingName",
    "At least one item has zero or negative quantity.": "errors.text.itemZeroQty",
    "At least one item has an invalid quantity.": "errors.text.itemInvalidQty",
    "Some items are missing required fields": "errors.text.itemsMissingFields",
    "All chunks failed to extract": "errors.text.allChunksFailed",
    "One or more chunks failed": "errors.text.someChunksFailed",
    "Extraction timed out.": "errors.text.extractionTimedOut",
    "end_time must be after start_time (set `next_day: true` for cross-midnight cooling).": "errors.text.endBeforeStartCooling",
    "check_date cannot be in the future": "errors.text.checkDateFuture",
    "Temperature log not found": "errors.text.temperatureLogNotFound",
    "Cooling log not found": "errors.text.coolingLogNotFound",
    "Equipment not found": "errors.text.equipmentNotFound",
    "No daily prep list for this date": "errors.text.noPrepListForDate",
    "Prep list not found": "errors.text.prepListNotFound",
    "Correcting a daily check requires an authorized role (corporate).": "errors.text.correctionRequiresRole",
    // L6: remaining user-facing server messages
    "Cannot refresh a completed inventory.": "errors.text.srv_Cannot_refresh_a_completed_inventory",
    "Assigned restaurant does not exist.": "errors.text.srv_Assigned_restaurant_does_not_exist",
    "Bug report not found": "errors.text.srv_Bug_report_not_found",
    "Cannot add items to a completed count": "errors.text.srv_Cannot_add_items_to_a_completed_count",
    "Cannot delete yourself": "errors.text.srv_Cannot_delete_yourself",
    "Cannot edit items on a completed count": "errors.text.srv_Cannot_edit_items_on_a_completed_count",
    "Cannot lock yourself": "errors.text.srv_Cannot_lock_yourself",
    "Cannot mark a cancelled invoice as paid": "errors.text.srv_Cannot_mark_a_cancelled_invoice_as_paid",
    "Cannot remove items from a completed count": "errors.text.srv_Cannot_remove_items_from_a_completed_count",
    "Capture not found": "errors.text.srv_Capture_not_found",
    "Category already exists": "errors.text.srv_Category_already_exists",
    "Category name is required": "errors.text.srv_Category_name_is_required",
    "Company not found": "errors.text.srv_Company_not_found",
    "Company not found.": "errors.text.srv_Company_not_found",
    "Completed counts cannot be deleted": "errors.text.srv_Completed_counts_cannot_be_deleted",
    "Count already completed": "errors.text.srv_Count_already_completed",
    "Count not found": "errors.text.srv_Count_not_found",
    "Cross-restaurant access denied": "errors.text.srv_Cross_restaurant_access_denied",
    "Day not found": "errors.text.srv_Day_not_found",
    "Dish name is required": "errors.text.srv_Dish_name_is_required",
    "Dish not found": "errors.text.srv_Dish_not_found",
    "Email already in use": "errors.text.srv_Email_already_in_use",
    "Empty barcode": "errors.text.srv_Empty_barcode",
    "Event not found": "errors.text.srv_Event_not_found",
    "Feature request not found": "errors.text.srv_Feature_request_not_found",
    "File not found on disk": "errors.text.srv_File_not_found_on_disk",
    "Forbidden": "errors.text.srv_Forbidden",
    "Ingredient not found": "errors.text.srv_Ingredient_not_found",
    "Invalid refresh token": "errors.text.srv_Invalid_refresh_token",
    "Invalid token": "errors.text.srv_Invalid_token",
    "Missing refresh token": "errors.text.srv_Missing_refresh_token",
    "Invoice not found": "errors.text.srv_Invoice_not_found",
    "Name is required": "errors.text.srv_Name_is_required",
    "No authorised restaurants": "errors.text.srv_No_authorised_restaurants",
    "No file captured yet": "errors.text.srv_No_file_captured_yet",
    "No recipient email available": "errors.text.srv_No_recipient_email_available",
    "No updates provided": "errors.text.srv_No_updates_provided",
    "Nothing to update": "errors.text.srv_Nothing_to_update",
    "Not authorised": "errors.text.srv_Not_authorised",
    "Not authorised for this restaurant": "errors.text.srv_Not_authorised_for_this_restaurant",
    "Not found": "errors.text.srv_Not_found",
    "Not your company": "errors.text.srv_Not_your_company",
    "Not your restaurant": "errors.text.srv_Not_your_restaurant",
    "Not your restaurant's recipe": "errors.text.srv_Not_your_restaurant_s_recipe",
    "Note text is required": "errors.text.srv_Note_text_is_required",
    "Only corporate users can create corporate recipes": "errors.text.srv_Only_corporate_users_can_create_corporate_recipes",
    "Password must be at least 6 characters": "errors.text.srv_Password_must_be_at_least_6_characters",
    "Passwords do not match": "errors.text.srv_Passwords_do_not_match",
    "Plan not found": "errors.text.srv_Plan_not_found",
    "Price must be > 0": "errors.text.srv_Price_must_be_0",
    "Product not found": "errors.text.srv_Product_not_found",
    "product not found": "errors.text.srv_product_not_found",
    "product is inactive": "errors.text.srv_product_is_inactive",
    "Product waste quantity must be > 0": "errors.text.srv_Product_waste_quantity_must_be_0",
    "Production review not found": "errors.text.srv_Production_review_not_found",
    "Recipe not found": "errors.text.srv_Recipe_not_found",
    "Restaurant not found": "errors.text.srv_Restaurant_not_found",
    "Restaurant not found.": "errors.text.srv_Restaurant_not_found",
    "Session ended": "errors.text.srv_Session_ended",
    "Session not found": "errors.text.srv_Session_not_found",
    "Session owner no longer exists": "errors.text.srv_Session_owner_no_longer_exists",
    "Subscription not found": "errors.text.srv_Subscription_not_found",
    "Super admin only": "errors.text.srv_Super_admin_only",
    "Supplier not found": "errors.text.srv_Supplier_not_found",
    "Support note not found": "errors.text.srv_Support_note_not_found",
    "Target menu not found": "errors.text.srv_Target_menu_not_found",
    "Target not found": "errors.text.srv_Target_not_found",
    "Task not found": "errors.text.srv_Task_not_found",
    "Too many submissions — slow down.": "errors.text.srv_Too_many_submissions_slow_down",
    "Unauthorized": "errors.text.srv_Unauthorized",
    "Unit code already exists": "errors.text.srv_Unit_code_already_exists",
    "User not found": "errors.text.srv_User_not_found",
    "Weekly menu not found": "errors.text.srv_Weekly_menu_not_found",
    "Wrong mode for this session": "errors.text.srv_Wrong_mode_for_this_session",
    "price must be >= 0": "errors.text.srv_price_must_be_0",
    "price must be numeric": "errors.text.srv_price_must_be_numeric",
    "sku not linked to product": "errors.text.srv_sku_not_linked_to_product",
    "unit cannot be empty": "errors.text.srv_unit_cannot_be_empty",
    "customers_target must not be negative": "errors.text.srv_customers_target_must_not_be_negative",
    "target_month is immutable": "errors.text.srv_target_month_is_immutable",
    "reason is required": "errors.text.srv_reason_is_required",
    "text is required": "errors.text.srv_text_is_required",
    "title is required": "errors.text.srv_title_is_required",
    "barcode is required": "errors.text.srv_barcode_is_required",
};

const STATUS_KEY = { 400: "badRequest", 401: "unauthorized", 403: "forbidden", 404: "notFound",
    409: "conflict", 413: "tooLarge", 422: "invalid", 429: "tooMany" };

export function apiErrorMessage(err) {
    const detail = err?.response?.data?.detail;
    if (!i18n.language || i18n.language === "en") return formatApiErrorDetail(detail);
    const code = detail && typeof detail === "object" && !Array.isArray(detail) ? detail.code : null;
    if (code && i18n.exists(`errors.codes.${code}`)) return i18n.t(`errors.codes.${code}`);
    if (typeof detail === "string" && KNOWN_TEXT[detail]) return i18n.t(KNOWN_TEXT[detail]);
    const status = err?.response?.status;
    if (!err?.response) return i18n.t("errors.network");
    if (STATUS_KEY[status]) return i18n.t(`errors.status.${STATUS_KEY[status]}`);
    return i18n.t("errors.generic");
}

// Raw `detail` (no axios error at hand): English unchanged; Finnish maps known code/text, else safe fallback.
export function translateDetail(detail) {
    if (!i18n.language || i18n.language === "en") return formatApiErrorDetail(detail);
    if (!detail) return "";
    const code = typeof detail === "object" && !Array.isArray(detail) ? detail.code : null;
    if (code && i18n.exists(`errors.codes.${code}`)) return i18n.t(`errors.codes.${code}`);
    if (typeof detail === "string" && KNOWN_TEXT[detail]) return i18n.t(KNOWN_TEXT[detail]);
    return i18n.t("errors.generic");
}

// Backend error text outside an axios error (e.g. a polled job's `error`).
export function translateErrorText(text) {
    if (!text || !i18n.language || i18n.language === "en") return text;
    return KNOWN_TEXT[text] ? i18n.t(KNOWN_TEXT[text]) : i18n.t("errors.generic");
}

// `detail || fallback` call sites: English unchanged; Finnish maps known code/text, else the (translated) fallback.
export function detailOr(detail, fallback) {
    if (!i18n.language || i18n.language === "en") return detail || fallback;
    const code = detail && typeof detail === "object" && !Array.isArray(detail) ? detail.code : null;
    if (code && i18n.exists(`errors.codes.${code}`)) return i18n.t(`errors.codes.${code}`);
    if (typeof detail === "string" && KNOWN_TEXT[detail]) return i18n.t(KNOWN_TEXT[detail]);
    return fallback || i18n.t("errors.generic");
}

export const _KNOWN_ERROR_TEXT_KEYS = Object.values(KNOWN_TEXT);
